import { Button } from './Button';
import { Modal } from './Modal';

interface EnrichHintDialogProps {
  isOpen: boolean;
  /** "brand", "manufacturer" — used in the copy. */
  entityNoun: string;
  /**
   * In-app links in the record's current description. When non-zero the dialog
   * warns that a rewrite could overwrite them and asks to Proceed.
   */
  descriptionLinkCount?: number;
  hint: string;
  onHintChange: (hint: string) => void;
  onCancel: () => void;
  onRun: () => void;
}

/**
 * The step before a brand or manufacturer AI enrich: an optional free-text
 * hint the model receives with the record (as "ADDITIONAL CONTEXT PROVIDED BY
 * USER"), mirroring the reference editor's pre-enrich dialog. It is the place
 * for what the model should know or check — not a field of the record.
 */
export function EnrichHintDialog({
  isOpen,
  entityNoun,
  descriptionLinkCount = 0,
  hint,
  onHintChange,
  onCancel,
  onRun,
}: EnrichHintDialogProps) {
  return (
    // A typed hint is lost to a stray backdrop click otherwise.
    <Modal isOpen={isOpen} onClose={onCancel} title='Enrich with AI' size='lg' dismissOnBackdrop={false}>
      <div className='space-y-4'>
        {descriptionLinkCount > 0 && <DescriptionLinksWarning count={descriptionLinkCount} />}
        <div>
          <label htmlFor='enrichHint' className='block text-sm font-medium mb-1'>
            Additional context (optional)
          </label>
          <textarea
            id='enrichHint'
            className='w-full border border-gray-300 rounded px-2 py-1 text-sm'
            rows={5}
            value={hint}
            onChange={(e) => onHintChange(e.target.value)}
            placeholder={`Anything the AI should know or check about this ${entityNoun} — sources, corrections, what to look for…`}
          />
          <p className='mt-1 text-xs text-gray-500'>
            Sent with this run only, alongside the {entityNoun}&apos;s current fields. Nothing here is saved to the {entityNoun}.
          </p>
        </div>
        <div className='flex justify-end gap-2 pt-2 border-t'>
          <Button variant='secondary' onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onRun}>{descriptionLinkCount > 0 ? 'Proceed' : 'Run enrichment'}</Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The pre-enrich warning for a record whose description carries in-app links.
 * Shared by the brand / manufacturer hint dialog and the parent-org setup.
 */
export function DescriptionLinksWarning({ count }: { count: number }) {
  return (
    <div role='alert' className='rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
      This description has {count} internal link{count === 1 ? '' : 's'}. Enrichment may drop{' '}
      {count === 1 ? 'it' : 'them'}. Proceed?
    </div>
  );
}
